import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Search } from 'lucide-react';
import type { HydratedVideoItem } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  groupContentErrorMessage,
  groupContentsVideoPickerQueryOptions,
  groupContentsVideoPlaylistsQueryOptions,
} from '@/lib/group-contents';

interface VideoPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alreadyPicked?: string[];
  onPick: (video: HydratedVideoItem) => void;
}

export function VideoPickerDialog({
  open,
  onOpenChange,
  alreadyPicked,
  onPick,
}: VideoPickerDialogProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [playlistId, setPlaylistId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => {
    if (open) {
      setQuery('');
      setPlaylistId(null);
    }
  }, [open]);

  const playlistsQuery = useQuery(groupContentsVideoPlaylistsQueryOptions);
  const pickerQuery = useQuery({
    ...groupContentsVideoPickerQueryOptions(debounced, playlistId),
    enabled: open,
  });

  const items = pickerQuery.data ?? [];
  const pickedSet = new Set(alreadyPicked ?? []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>เลือกวิดีโอ</DialogTitle>
          <DialogDescription>
            ค้นหาจาก youtube_videos ที่ poll มาแล้ว (ไม่รวม video ที่ถูกซ่อน)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="พิมพ์ชื่อวิดีโอ..."
                autoFocus
                className="pl-9"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
            <select
              value={playlistId ?? ''}
              onChange={(e) => setPlaylistId(e.target.value || null)}
              className="border-input bg-background h-10 rounded-md border px-3 text-sm"
            >
              <option value="">ทุก playlist</option>
              {(playlistsQuery.data ?? []).map((pl) => (
                <option key={pl.id} value={pl.id}>
                  {pl.name}
                </option>
              ))}
            </select>
          </div>

          {pickerQuery.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{groupContentErrorMessage(pickerQuery.error)}</AlertDescription>
            </Alert>
          )}

          <div className="max-h-96 overflow-y-auto rounded-md border border-slate-200 dark:border-slate-800">
            {pickerQuery.isLoading ? (
              <div className="p-6 text-center text-sm text-slate-500">กำลังโหลด...</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">
                {debounced
                  ? `ไม่พบวิดีโอ "${debounced}"`
                  : playlistId
                    ? 'playlist นี้ยังไม่มีวิดีโอ'
                    : 'ยังไม่มีวิดีโอใน youtube_videos'}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((v) => {
                  const isDup = pickedSet.has(v.id);
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        disabled={isDup}
                        onClick={() => {
                          onPick(v);
                          onOpenChange(false);
                        }}
                        className={`flex w-full items-start gap-3 p-3 text-left transition ${
                          isDup
                            ? 'cursor-not-allowed opacity-40'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                        }`}
                      >
                        <img
                          src={v.thumbnailUrl}
                          alt=""
                          loading="lazy"
                          className="h-12 w-20 shrink-0 rounded object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.visibility = 'hidden';
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="line-clamp-2 text-sm font-medium text-slate-900 dark:text-slate-50">
                            {v.title}
                          </div>
                          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                            {v.publishedAt && (
                              <span>
                                {new Date(v.publishedAt).toLocaleDateString('th-TH', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: '2-digit',
                                })}
                              </span>
                            )}
                            {v.channelTitle && <span>· {v.channelTitle}</span>}
                            {isDup && (
                              <span className="text-amber-600 dark:text-amber-400">
                                · อยู่ใน group นี้แล้ว
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ปิด
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
